'use client'

import type { BookDetails } from '@package/api'
import { BookPremiumCorner } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { BookCoverCard } from './cards/book-cover-card'
import { ContentStats } from './content-stats'
import { Tag } from './tag'

interface BookDetailBodyProps {
  book: BookDetails
  /** Optional popularity row. Tags stay non-clickable when `onTagClick` is omitted. */
  stats?: { likes?: number | null; views?: number | null }
  onTagClick?: (type: 'author' | 'publisher' | 'level' | 'theme', id: string) => void
}

/** Presentational two-column book detail — cover + title/meta/synopsis/tags.
 * Shared between the book detail modal and the public `/buku` showcase page. */
export function BookDetailBody({ book, stats, onTagClick }: BookDetailBodyProps) {
  const publishedYear = book.bookPublishedOn ? new Date(book.bookPublishedOn).getFullYear() : null
  const metaLine = [publishedYear, book.page ? `${book.page} halaman` : null].filter(Boolean).join(' • ')

  return (
    <div className='srd:flex srd:flex-col srd:md:flex-row srd:gap-0'>
      <div className='srd:flex srd:items-center srd:justify-center srd:p-6 srd:md:w-55 srd:md:shrink-0'>
        <div className='srd:relative srd:w-45 srd:md:w-full'>
          <BookCoverCard id={book.id} coverUrl={book.coverUrl} className='srd:w-full srd:h-auto srd:aspect-3/4' />
          {!book.isFree && (
            <div className='srd:absolute srd:bottom-0 srd:right-0 srd:overflow-hidden srd:rounded-br-xl'>
              <BookPremiumCorner size={56} />
            </div>
          )}
        </div>
      </div>

      <div className='srd:flex srd:flex-col srd:gap-y-2 srd:p-6 srd:flex-1 srd:min-w-0'>
        <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>{book.title}</h2>

        {metaLine && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{metaLine}</p>
        )}

        {stats && <ContentStats likes={stats.likes} views={stats.views} className='srd:text-neutral-black' />}

        <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black srd:line-clamp-4')}>
          {book.synopsis}
        </p>

        <div className='srd:flex srd:flex-wrap srd:gap-x-2 srd:gap-y-1.5 srd:mt-1'>
          {book.authors.map((author) => (
            <Tag
              key={author.id}
              label={author.name}
              onClick={onTagClick ? () => onTagClick('author', author.id) : undefined}
            />
          ))}

          {book.publisher && (
            <Tag
              label={book.publisher.name}
              onClick={onTagClick ? () => onTagClick('publisher', book.publisher.id) : undefined}
              iconUrl={book.publisher.logo ?? undefined}
            />
          )}

          {book.bookToBookThemes.map(({ bookTheme }) => (
            <Tag
              key={bookTheme.id}
              label={bookTheme.name}
              onClick={onTagClick ? () => onTagClick('theme', bookTheme.id) : undefined}
              iconUrl={(bookTheme.iconUrl as string | null) ?? undefined}
            />
          ))}

          {book.level && (
            <Tag label={book.level.name} onClick={onTagClick ? () => onTagClick('level', book.level.id) : undefined} />
          )}
        </div>
      </div>
    </div>
  )
}
